import * as B from '@blueprintjs/core';
import {DateRange} from '@blueprintjs/datetime2';
import classNames from 'classnames';
import moment from 'moment';
import React from 'react';
import {useNavigate} from 'react-router-dom';

import {
  AGENT_CUSTOM_PROMPT_KEY,
  AGENT_USE_CASES,
  getUseCaseCopy,
} from 'app/components/AgentExplorer/PromptView/promptConfigs';
import {ExploreRunPrompt} from 'app/components/AgentExplorer/types';
import * as routeUtils from 'app/utils/routeUtils';

import {useCreateAgentWorkflow} from '../hooks';
import {DetailsStep} from './DetailsStep';
import {ImageryStep} from './ImageryStep';
import {PropertiesStep} from './PropertiesStep';
import cs from './styles.module.scss';
import {ProjectOption, RunPlanDraft} from './types';
import {WhatToLookForStep} from './WhatToLookForStep';

// Imagery comes first: how much imagery a run reads decides which prompts it can be asked to run.
const STEPS = ['Imagery', 'What to look for', 'Properties', 'Name and schedule'];

const isCompleteRange = (range: DateRange) => !!range[0] && !!range[1];

// Windows are stored on the plan, formatted %m/%d/%Y, and apply to every one of its runs. A
// single-scene plan sends only the after window: it's the one the agent reads, and the API rejects
// a before window alongside it.
const toDateWindows = (draft: RunPlanDraft) => {
  const format = (range: DateRange) =>
    range.map((date) => moment(date).format('MM/DD/YYYY')) as [string, string];
  return {
    afterDateRange: format(draft.afterDateRange),
    ...(draft.sceneMode === 'compare' && {beforeDateRange: format(draft.beforeDateRange)}),
  };
};

// The cap a plan puts on imagery resolution, in m/px
const toMaxSceneResolution = (draft: RunPlanDraft): number | null => {
  if (draft.resolutionChoice === 'any') {
    return null;
  }
  const meters =
    draft.resolutionChoice === 'custom'
      ? Number(draft.customResolution)
      : Number(draft.resolutionChoice);
  return Number.isFinite(meters) && meters > 0 ? meters : null;
};

const StepRail: React.FC<{step: number; onGo: (step: number) => void}> = ({step, onGo}) => (
  <div className={cs.stepRail}>
    {STEPS.map((label, i) => {
      const visited = i < step;
      const current = i === step;
      return (
        <React.Fragment key={label}>
          {i > 0 && <span className={cs.stepRailConnector} />}
          <button
            className={classNames(cs.stepRailStep, {
              [cs.stepRailStepVisited]: visited,
              [cs.stepRailStepCurrent]: current,
            })}
            onClick={() => visited && onGo(i)}
            disabled={!visited}
          >
            <span
              className={classNames(cs.stepRailMarker, {
                [cs.stepRailMarkerDone]: visited,
                [cs.stepRailMarkerCurrent]: current,
              })}
            >
              {visited ? <B.Icon icon="small-tick" size={12} /> : i + 1}
            </span>
            {label}
          </button>
        </React.Fragment>
      );
    })}
  </div>
);

export const NewWorkflowModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  projectOptions: ProjectOption[];
  organizationId: string;
  // Free-text prompts are restricted to Upstream staff, matching what the API accepts.
  canUseCustomPrompt: boolean;
}> = ({isOpen, onClose, projectOptions, organizationId, canUseCustomPrompt}) => {
  const navigate = useNavigate();
  const [step, setStep] = React.useState(0);
  const [draft, setDraft] = React.useState<RunPlanDraft>({
    sceneMode: 'compare',
    imageryWindow: 'latest',
    beforeDateRange: [null, null],
    afterDateRange: [null, null],
    resolutionChoice: 'any',
    customResolution: '',
    promptKey: null,
    customPromptText: '',
    customPromptConcepts: [],
    customPromptAgentType: 'describe',
    selectedFcId: projectOptions[0].fcId,
    selectedFeatures: [],
    title: '',
  });

  const patchDraft = React.useCallback(
    (patch: Partial<RunPlanDraft>) => setDraft((d) => ({...d, ...patch})),
    []
  );

  // Which button was pressed, so only that one spins while the shared mutation runs.
  const [pendingAction, setPendingAction] = React.useState<'save' | 'run' | null>(null);
  const {
    mutateAsync: createWorkflow,
    isPending: isSubmitting,
    error,
  } = useCreateAgentWorkflow(organizationId);

  const isCustom = draft.promptKey === AGENT_CUSTOM_PROMPT_KEY;
  const isCustomSegmentation = isCustom && draft.customPromptAgentType === 'identify';
  // Named for where the properties came from, not the portfolio the dropdown happens to be on.
  const selectedFcIds = [
    ...new Set(draft.selectedFeatures.map((f) => String(f.properties.featureCollectionId))),
  ];
  const portfolioNames = (selectedFcIds.length > 0 ? selectedFcIds : [draft.selectedFcId]).map(
    (fcId) => projectOptions.find((p) => p.fcId === fcId)?.name ?? ''
  );
  const portfolioName =
    portfolioNames.length > 1
      ? `${portfolioNames[0]} and ${portfolioNames.length - 1} more ${
          portfolioNames.length === 2 ? 'portfolio' : 'portfolios'
        }`
      : portfolioNames[0];
  const presetUseCase = AGENT_USE_CASES.find((u) => u.key === draft.promptKey);
  const presetLabel = presetUseCase
    ? getUseCaseCopy(presetUseCase, draft.sceneMode === 'compare').label
    : isCustomSegmentation
      ? 'Custom segmentation'
      : 'Custom analysis';
  const suggestedTitle = `${presetLabel} — ${portfolioName}`;

  const usesDateRanges = draft.imageryWindow === 'dateRange';
  const maxSceneResolution = toMaxSceneResolution(draft);
  const stepReady = [
    (!usesDateRanges ||
      (isCompleteRange(draft.afterDateRange) &&
        (draft.sceneMode === 'single' || isCompleteRange(draft.beforeDateRange)))) &&
      (draft.resolutionChoice !== 'custom' || maxSceneResolution !== null),
    !!draft.promptKey &&
      (!isCustom ||
        (isCustomSegmentation
          ? draft.customPromptConcepts.length > 0
          : draft.customPromptText.trim().length > 0)),
    draft.selectedFeatures.length > 0,
    draft.title.trim().length > 0,
  ];

  const handleSubmit = async (startRun: boolean) => {
    // Segmentation concepts travel as the comma-separated userPrompt the API splits back apart.
    const prompt: ExploreRunPrompt = isCustom
      ? {
          userPrompt: isCustomSegmentation
            ? draft.customPromptConcepts.join(', ')
            : draft.customPromptText.trim(),
          agentType: draft.customPromptAgentType,
        }
      : {promptKey: draft.promptKey!};
    setPendingAction(startRun ? 'run' : 'save');
    try {
      const {workflow, run} = await createWorkflow({
        featureIds: draft.selectedFeatures.map((f) => f.properties.id),
        title: draft.title.trim(),
        sceneMode: draft.sceneMode,
        ...(usesDateRanges && toDateWindows(draft)),
        ...(maxSceneResolution !== null && {maxSceneResolution}),
        ...prompt,
        startRun,
      });
      const orgIdPrefix = routeUtils.makeUuidPrefix(organizationId);
      // A plan saved without a run has nothing to show yet, so land on the plan itself.
      navigate(
        run
          ? `/${orgIdPrefix}/agent/workflows/${workflow.id}/runs/${run.id}`
          : `/${orgIdPrefix}/agent/workflows/${workflow.id}`
      );
      onClose();
    } catch {
      // The mutation's error is rendered below; leave the dialog open so the draft isn't lost.
    }
  };

  return (
    <B.Dialog
      className={cs.dialog}
      isOpen={isOpen}
      onClose={onClose}
      title="New run plan"
      canOutsideClickClose={false}
    >
      <B.DialogBody>
        <StepRail step={step} onGo={setStep} />
        {step === 0 && <ImageryStep draft={draft} setDraft={patchDraft} />}
        {step === 1 && (
          <WhatToLookForStep
            draft={draft}
            setDraft={patchDraft}
            canUseCustomPrompt={canUseCustomPrompt}
          />
        )}
        {step === 2 && (
          <PropertiesStep draft={draft} setDraft={patchDraft} projectOptions={projectOptions} />
        )}
        {step === 3 && (
          <DetailsStep draft={draft} setDraft={patchDraft} suggestedTitle={suggestedTitle} />
        )}
        {error && (
          <B.Callout intent="danger" icon="error">
            {(error as Error).message || 'Failed to create the run plan. Please try again.'}
          </B.Callout>
        )}
      </B.DialogBody>
      <B.DialogFooter
        actions={
          <>
            {step > 0 && (
              <B.Button icon="chevron-left" text="Back" onClick={() => setStep(step - 1)} />
            )}
            {step < STEPS.length - 1 ? (
              <B.Button
                intent={B.Intent.PRIMARY}
                rightIcon="chevron-right"
                text="Continue"
                disabled={!stepReady[step]}
                onClick={() => setStep(step + 1)}
              />
            ) : (
              <>
                <B.Button
                  text="Save without running"
                  disabled={!stepReady[step] || isSubmitting}
                  loading={isSubmitting && pendingAction === 'save'}
                  onClick={() => handleSubmit(false)}
                />
                <B.Button
                  className={cs.runButton}
                  intent={B.Intent.PRIMARY}
                  icon="clean"
                  text={`Save and run on ${draft.selectedFeatures.length} ${
                    draft.selectedFeatures.length === 1 ? 'property' : 'properties'
                  }`}
                  disabled={!stepReady[step] || isSubmitting}
                  loading={isSubmitting && pendingAction === 'run'}
                  onClick={() => handleSubmit(true)}
                />
              </>
            )}
          </>
        }
      />
    </B.Dialog>
  );
};
